import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { scratchService } from '../services/api.service';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

const MyPrizesModal = ({ isOpen, onClose, prizes: initialPrizes = [], user }) => {
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [prizes, setPrizes] = useState(initialPrizes);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPrizes = async () => {
      try {
        const response = await scratchService.getMyPrizes();
        if (response.success && Array.isArray(response.prizes)) {
          setPrizes(response.prizes);
        } else {
          setPrizes([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error cargando premios:', error);
        setPrizes([]);
        setLoading(false);
      }
    };

    if (isOpen) {
      loadPrizes();
    }
  }, [isOpen]);

  const handleWhatsAppShare = (prize) => {
    console.log('User data:', user?.userData);
    
    // Obtener y limpiar el número de teléfono
    let phoneNumber = user?.userData?.phone || '';
    phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, ''); // Eliminar espacios, guiones y paréntesis
    
    console.log('Phone number after cleanup:', phoneNumber);

    if (!phoneNumber) {
      toast.error('No se encontró un número de teléfono válido');
      return;
    }

    // Asegurarnos de que el número empiece con el código de país
    let formattedPhone = phoneNumber;
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = formattedPhone.startsWith('34') ? formattedPhone : `34${formattedPhone}`;
    }
    formattedPhone = formattedPhone.replace('+', ''); // Eliminar el + si existe
    
    console.log('Formatted phone:', formattedPhone);

    const message = encodeURIComponent(
      `🎉 ¡Has ganado un premio!\n\n` +
      `Premio: ${prize.name}\n` +
      `Código: ${prize.code}\n\n` +
      `Instrucciones de canjeo:\n${prize.campaign?.redeemInstructions || 'Contacta con el establecimiento'}`
    );

    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
    console.log('WhatsApp URL:', whatsappUrl);

    // Abrir WhatsApp en una nueva ventana
    const newWindow = window.open(whatsappUrl, '_blank');
    
    if (!newWindow) {
      toast.error('El navegador bloqueó la apertura de WhatsApp. Por favor, permite las ventanas emergentes.');
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPrizeIcon = (prize) => {
    if (!prize) return '🎁';
    return prize.type === 'coupon' ? '🎫' : '🎁';
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Mis Premios</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <PrizesList>
          {loading ? (
            <LoadingMessage>Cargando premios...</LoadingMessage>
          ) : prizes.length === 0 ? (
            <NoPrizes>Aún no has ganado ningún premio</NoPrizes>
          ) : (
            prizes.map((prize, index) => (
              <PrizeCard key={index}>
                <PrizeHeader>
                  <PrizeInfo>
                    <PrizeName>{prize.name}</PrizeName>
                    {prize.code && <PrizeCode>#{prize.code}</PrizeCode>}
                    {prize.campaignName && (
                      <CampaignName>{prize.campaignName}</CampaignName>
                    )}
                    {prize.participationDate && (
                      <PrizeDate>
                        Ganado el {formatDate(prize.participationDate)}
                      </PrizeDate>
                    )}
                  </PrizeInfo>
                </PrizeHeader>
                
                <ButtonsContainer>
                  <PrizeStatus claimed={prize.claimed}>
                    <StatusIcon>{prize.claimed ? '✓' : '⌛'}</StatusIcon>
                    <StatusText>
                      {prize.claimed ? 'Canjeado' : 'Pendiente'}
                    </StatusText>
                  </PrizeStatus>
                  <InstructionsButton onClick={() => setSelectedPrize(prize)}>
                    <span>📋</span>
                    <span>Ver instrucciones</span>
                  </InstructionsButton>
                </ButtonsContainer>
              </PrizeCard>
            ))
          )}
        </PrizesList>

        <AnimatePresence>
          {selectedPrize && (
            <InstructionsOverlay onClick={() => setSelectedPrize(null)}>
              <InstructionsModal
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
              >
                <InstructionsHeader>
                  <InstructionsTitle>{selectedPrize.name}</InstructionsTitle>
                  <CloseButton onClick={() => setSelectedPrize(null)}>&times;</CloseButton>
                </InstructionsHeader>
                
                <InstructionsContent>
                  <CodeSection>
                    <Label>Código del premio:</Label>
                    <Code>{selectedPrize.code}</Code>
                  </CodeSection>

                  <InstructionsSection>
                    <Label>Instrucciones de canjeo:</Label>
                    <Instructions>
                      {selectedPrize.campaign?.redeemInstructions || 
                       'Contacta con el establecimiento para canjear tu premio.'}
                    </Instructions>
                  </InstructionsSection>

                  <WhatsAppButton onClick={() => handleWhatsAppShare(selectedPrize)}>
                    <WhatsAppIcon>📱</WhatsAppIcon>
                    <span>Enviarme por WhatsApp</span>
                  </WhatsAppButton>
                </InstructionsContent>
              </InstructionsModal>
            </InstructionsOverlay>
          )}
        </AnimatePresence>
      </ModalContent>
    </ModalOverlay>
  );
};

// Estilos consistentes con el diseño actual
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(76, 29, 149, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, 
    rgba(88, 28, 135, 0.95) 0%,
    rgba(67, 20, 102, 0.95) 100%
  );
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 8px 32px rgba(76, 29, 149, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  font-family: 'BaksoSapi', sans-serif;
  color: #ffd700;
  font-size: 1.5rem;
  margin: 0;
  letter-spacing: 1px;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 215, 0, 0.8);
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: all 0.3s ease;

  &:hover {
    color: #ffd700;
    transform: rotate(90deg);
  }
`;

const PrizesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const PrizeCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrizeHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const PrizeInfo = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const PrizeName = styled.div`
  color: #ffd700;
  font-size: 1.2rem;
  font-weight: 500;
`;

const CampaignName = styled.div`
  color: rgba(255, 215, 0, 0.7);
  font-size: 0.9rem;
  margin: 0.2rem 0;
`;

const PrizeDate = styled.div`
  color: rgba(255, 215, 0, 0.5);
  font-size: 0.8rem;
  margin-top: 0.2rem;
`;

const ButtonBase = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PrizeStatus = styled(ButtonBase)`
  background: ${props => props.claimed ? 
    'rgba(74, 222, 128, 0.2)' : 
    'rgba(255, 215, 0, 0.1)'
  };
  color: ${props => props.claimed ? '#4ade80' : '#ffd700'};
  border: 1px solid ${props => props.claimed ? 
    'rgba(74, 222, 128, 0.3)' : 
    'rgba(255, 215, 0, 0.2)'
  };
  cursor: default;

  &:hover {
    transform: none;
    background: ${props => props.claimed ? 
      'rgba(74, 222, 128, 0.2)' : 
      'rgba(255, 215, 0, 0.1)'
    };
  }
`;

const InstructionsButton = styled(ButtonBase)``;

const StatusIcon = styled.span`
  font-size: 1.2rem;
`;

const StatusText = styled.span`
  font-size: 0.9rem;
`;

const InstructionsOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const InstructionsModal = styled(motion.div)`
  position: relative;
  background: linear-gradient(135deg, 
    rgba(88, 28, 135, 0.95) 0%,
    rgba(67, 20, 102, 0.95) 100%
  );
  padding: 2rem;
  border-radius: 20px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 8px 32px rgba(76, 29, 149, 0.5);
  backdrop-filter: blur(10px);

  @media (max-width: 480px) {
    width: 85%;
    padding: 1.5rem;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const InstructionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
  padding-bottom: 1rem;
`;

const InstructionsTitle = styled.h2`
  font-family: 'BaksoSapi', sans-serif;
  color: #ffd700;
  margin: 0;
  font-size: 1.5rem;
  letter-spacing: 1px;
`;

const InstructionsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0 1rem;
`;

const CodeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
`;

const Code = styled.div`
  font-family: monospace;
  font-size: 2rem;
  color: #ffd700;
  text-align: center;
  letter-spacing: 3px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const InstructionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
`;

const Instructions = styled.div`
  color: #fff;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const Label = styled.div`
  color: rgba(255, 215, 0, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const WhatsAppButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: #25D366;
  color: white;
  border: none;
  padding: 1.2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);

  &:hover {
    background: #128C7E;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(37, 211, 102, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const WhatsAppIcon = styled.span`
  font-size: 1.4rem;
`;

const NoPrizes = styled.div`
  color: rgba(255, 215, 0, 0.6);
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
`;

const LoadingMessage = styled.div`
  color: rgba(255, 215, 0, 0.6);
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
`;

const PrizeCode = styled.div`
  color: rgba(255, 215, 0, 0.6);
  font-size: 0.85rem;
  font-family: monospace;
  background: rgba(255, 215, 0, 0.1);
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  margin: 0.2rem 0;
  display: inline-block;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`;

export default MyPrizesModal;